function createCrudEndpoint<TBaseUrl extends string>(baseUrl: TBaseUrl) {
  return {
    list: `${baseUrl}/list`,
    detail: `${baseUrl}/detail`,
    create: `${baseUrl}/create`,
    update: `${baseUrl}/update`,
    remove: `${baseUrl}/remove`,
  } as const
}

type JoinPath<TBase extends string, TPath extends string> = TBase extends `${infer R}/`
  ? `${R}/${TPath}` // If Base ends with '/', remove it before adding path
  : TBase extends ''
    ? TPath // If Base is empty, just use the path
    : `${TBase}/${TPath}` // Otherwise, join with a single '/'

// 2. Main Type: Creates the Mapped Object Type
// It iterates over the array of paths and applies JoinPath for each.
type EndpointMap<TBaseUrl extends string, TPaths extends ReadonlyArray<string>> = {
  [K in TPaths[number]]: JoinPath<TBaseUrl, K>
}

function buildEndpoint<TBaseUrl extends string, TPaths extends ReadonlyArray<string>>(
  baseUrl: TBaseUrl,
  paths: TPaths
): EndpointMap<TBaseUrl, TPaths> {
  // Runtime logic: a bit of simple mapping to create the actual URLs.
  const result = paths.reduce((acc, path) => {
    // Basic path joining logic, avoiding double slashes is a common practice.
    const joinedUrl = baseUrl.endsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`

    // The type assertion is necessary here because the runtime (JavaScript)
    // doesn't know about the complex type mapping we defined.
    // We trust that our types (EndpointMap) accurately describe the runtime output.
    acc[path] = joinedUrl
    return acc
  }, {} as any) // Start with 'any' or an empty object type and let the final return cast work

  return result as EndpointMap<TBaseUrl, TPaths>
}

const ENDPOINT = {
  auth: {
    login: 'auth/login',
  },
  account: {
    admin: createCrudEndpoint('account/admin'),
    user: {
      detail: 'account/user',
    },
  },
  outlet: {
    master: createCrudEndpoint('outlet'),
    device: createCrudEndpoint('outlet/device'),
  },
  product: {
    master: createCrudEndpoint('product/master'),
    category: createCrudEndpoint('product/category'),
  },
  inventory: {
    uom: createCrudEndpoint('inventory/uom'),
    category: createCrudEndpoint('inventory/item-category'),
    master: createCrudEndpoint('inventory/item-master'),
    outlet: buildEndpoint('inventory/item-outlet', ['list', 'detail', 'assign'] as const),
    purchaseOrder: createCrudEndpoint('inventory/purchase-order'),
  },
  moka: {
    outlet: {
      connect: 'moka/outlet/connect',
      detail: 'moka/outlet/detail',
    },
    workflow: {
      list: 'moka/workflow/list',
      trigger: 'moka/workflow/trigger',
    },
    data: {
      listSales: 'moka/data/list/sales',
    },
  },
} as const

export { ENDPOINT }
